import React, { useState } from 'react';
import { BookOpen, Puzzle, Link2, FileText, ChevronDown, ChevronUp, Lightbulb, Eye, Target, LayoutGrid, GraduationCap, Settings, Layers, CheckCircle, Library, HelpCircle, ArrowRight, Boxes, Check } from 'lucide-react';
import { useWizard } from '../../context/WizardContext';

const FrameworkPage = ({ onLaunchWizardC }) => {
  const { switchWizard, frameworkComplete, setFrameworkComplete } = useWizard();
  
  const handleLaunchWizardC = () => {
    switchWizard('C');
    if (onLaunchWizardC) {
      onLaunchWizardC();
    }
  };
  const [expandedSections, setExpandedSections] = useState({
    definition: true,
    constructs: true,
    relationships: true,
    propositions: true,
    assumptions: true,
    boundaries: true,
    visual: true,
    theoretical: true,
    context: true,
    levels: true
  });

  const analysisLevels = [
    { id: 'individual', level: 'Individual', unit: 'Person', examples: 'Personality, attitudes, behavior' },
    { id: 'interpersonal', level: 'Interpersonal', unit: 'Dyad/Group', examples: 'Communication, conflict, trust' },
    { id: 'team', level: 'Team', unit: 'Work group', examples: 'Team cohesion, performance' },
    { id: 'organizational', level: 'Organizational', unit: 'Company', examples: 'Culture, structure, strategy' },
    { id: 'institutional', level: 'Institutional', unit: 'Industry/Sector', examples: 'Norms, regulations, standards' },
    { id: 'societal', level: 'Societal', unit: 'Nation/Culture', examples: 'Values, laws, economic systems' }
  ];


  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const relationshipTypes = [
    { type: 'Causal', symbol: 'A → B', meaning: 'A causes/influences B', example: 'Training → Performance' },
    { type: 'Correlational', symbol: 'A ↔ B', meaning: 'A and B are related', example: 'Stress ↔ Burnout' },
    { type: 'Mediating', symbol: 'A → M → B', meaning: "M explains A's effect on B", example: 'Exercise → Endorphins → Mood' },
    { type: 'Moderating', symbol: 'A × M → B', meaning: "M changes A's effect on B", example: 'Study time × Intelligence → Grades' },
    { type: 'Hierarchical', symbol: 'A > B > C', meaning: 'Nested levels', example: 'Country > School > Student' },
    { type: 'Sequential', symbol: 'A ⟹ B ⟹ C', meaning: 'Time-ordered', example: 'Awareness ⟹ Interest ⟹ Purchase' }
  ];

  return (
    <div className="framework-page">
      <div className="framework-header">
        <h1>
          <BookOpen size={32} />
          Understanding Research Frameworks
        </h1>
        <p className="framework-intro">
          Learn the essential components that form the backbone of your research design. 
          A well-constructed framework provides clarity, structure, and direction for your study.
        </p>
      </div>

      <div className="framework-sections">
        <section className="framework-section">
          <div 
            className="section-header"
            onClick={() => toggleSection('definition')}
          >
            <div className="section-title">
              <div className="section-icon definition-icon">
                <Lightbulb size={24} />
              </div>
              <div>
                <h2>What IS a Framework?</h2>
                <span className="section-subtitle">The Foundation of Your Research</span>
              </div>
            </div>
            {expandedSections.definition ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
          </div>

          {expandedSections.definition && (
            <div className="section-content">
              <div className="definition-box">
                <p className="definition-main">
                  A framework is a <strong>structured system</strong> that organizes your research 
                  and shows how different elements connect to each other.
                </p>
              </div>

              <div className="framework-functions">
                <h3>A Framework Does Four Key Things:</h3>
                <div className="function-grid">
                  <div className="function-card">
                    <div className="function-number">1</div>
                    <div className="function-text">
                      <strong>Organizes</strong> complex concepts into a coherent structure
                    </div>
                  </div>
                  <div className="function-card">
                    <div className="function-number">2</div>
                    <div className="function-text">
                      <strong>Shows relationships</strong> between elements
                    </div>
                  </div>
                  <div className="function-card">
                    <div className="function-number">3</div>
                    <div className="function-text">
                      <strong>Guides</strong> thinking, analysis, or action
                    </div>
                  </div>
                  <div className="function-card">
                    <div className="function-number">4</div>
                    <div className="function-text">
                      <strong>Provides a lens</strong> for understanding phenomena
                    </div>
                  </div>
                </div>
              </div>

              <div className="analogy-box">
                <div className="analogy-icon">💡</div>
                <div className="analogy-content">
                  <strong>Think of it as:</strong> The "skeleton" or "scaffolding" that holds 
                  your research together. Just as a building needs a structural frame before 
                  walls can be added, your research needs a framework before data can be collected 
                  and analyzed meaningfully.
                </div>
              </div>
            </div>
          )}
        </section>

        <section className="framework-section">
          <div 
            className="section-header"
            onClick={() => toggleSection('constructs')}
          >
            <div className="section-title">
              <div className="section-icon constructs-icon">
                <Puzzle size={24} />
              </div>
              <div>
                <h2>1. Constructs and Concepts</h2>
                <span className="section-subtitle">The Building Blocks</span>
              </div>
            </div>
            {expandedSections.constructs ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
          </div>

          {expandedSections.constructs && (
            <div className="section-content">
              <div className="concept-definition">
                <h3>What Are Constructs?</h3>
                <p>
                  Constructs are the <strong>main ideas, variables, or phenomena</strong> you're 
                  examining in your research. They are the fundamental concepts that your study 
                  is built around.
                </p>
              </div>

              <div className="examples-box">
                <h4>Examples of Constructs:</h4>
                <div className="construct-examples">
                  <div className="construct-category">
                    <span className="category-label">Psychology:</span>
                    <span className="category-items">Self-efficacy, Motivation, Anxiety, Job satisfaction</span>
                  </div>
                  <div className="construct-category">
                    <span className="category-label">Education:</span>
                    <span className="category-items">Learning outcomes, Student engagement, Teaching effectiveness</span>
                  </div>
                  <div className="construct-category">
                    <span className="category-label">Business:</span>
                    <span className="category-items">Organizational culture, Leadership style, Employee performance</span>
                  </div>
                  <div className="construct-category">
                    <span className="category-label">Health:</span>
                    <span className="category-items">Quality of life, Treatment adherence, Patient satisfaction</span>
                  </div>
                </div>
              </div>

              <div className="tip-box">
                <strong>Key Insight:</strong> Constructs are often abstract ideas that cannot be 
                directly observed. They need to be "operationalized" (defined in measurable terms) 
                before they can be studied. For example, "intelligence" might be operationalized 
                as an IQ score.
              </div>
            </div>
          )}
        </section>

        <section className="framework-section">
          <div 
            className="section-header"
            onClick={() => toggleSection('relationships')}
          >
            <div className="section-title">
              <div className="section-icon relationships-icon">
                <Link2 size={24} />
              </div>
              <div>
                <h2>2. Relationships</h2>
                <span className="section-subtitle">How Things Connect</span>
              </div>
            </div>
            {expandedSections.relationships ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
          </div>

          {expandedSections.relationships && (
            <div className="section-content">
              <div className="concept-definition">
                <h3>What Are Relationships?</h3>
                <p>
                  Relationships describe the <strong>connections, links, or associations</strong> between 
                  your constructs. Understanding these relationships is central to answering your research 
                  questions.
                </p>
              </div>

              <div className="relationship-table-container">
                <h4>Types of Relationships:</h4>
                <table className="relationship-table">
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Symbol</th>
                      <th>Meaning</th>
                      <th>Example</th>
                    </tr>
                  </thead>
                  <tbody>
                    {relationshipTypes.map((rel, index) => (
                      <tr key={index}>
                        <td className="rel-type">{rel.type}</td>
                        <td className="rel-symbol">{rel.symbol}</td>
                        <td className="rel-meaning">{rel.meaning}</td>
                        <td className="rel-example">{rel.example}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="relationship-explanations">
                <div className="rel-explanation-card">
                  <h5>Causal Relationships</h5>
                  <p>One variable directly influences or causes changes in another. Requires experimental design to establish.</p>
                </div>
                <div className="rel-explanation-card">
                  <h5>Mediating Relationships</h5>
                  <p>A third variable (mediator) explains WHY or HOW one variable affects another.</p>
                </div>
                <div className="rel-explanation-card">
                  <h5>Moderating Relationships</h5>
                  <p>A third variable (moderator) changes the STRENGTH or DIRECTION of a relationship between two variables.</p>
                </div>
              </div>
            </div>
          )}
        </section>

        <section className="framework-section">
          <div 
            className="section-header"
            onClick={() => toggleSection('propositions')}
          >
            <div className="section-title">
              <div className="section-icon propositions-icon">
                <FileText size={24} />
              </div>
              <div>
                <h2>3. Propositions and Hypotheses</h2>
                <span className="section-subtitle">Expected Patterns</span>
              </div>
            </div>
            {expandedSections.propositions ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
          </div>

          {expandedSections.propositions && (
            <div className="section-content">
              <div className="concept-definition">
                <h3>What Are Propositions?</h3>
                <p>
                  Propositions are <strong>statements about how constructs relate</strong> to each other. 
                  They express your expectations about what patterns you'll find in your research.
                </p>
              </div>

              <div className="proposition-examples">
                <h4>Example Propositions:</h4>
                <div className="proposition-list">
                  <div className="proposition-item">
                    <span className="proposition-quote">"Higher teacher self-efficacy leads to better student outcomes"</span>
                    <span className="proposition-type">Causal proposition</span>
                  </div>
                  <div className="proposition-item">
                    <span className="proposition-quote">"Organizational culture mediates the relationship between leadership and innovation"</span>
                    <span className="proposition-type">Mediation proposition</span>
                  </div>
                  <div className="proposition-item">
                    <span className="proposition-quote">"Social support buffers the negative effects of stress on mental health"</span>
                    <span className="proposition-type">Moderation proposition</span>
                  </div>
                </div>
              </div>

              <div className="proposition-forms">
                <h4>Forms of Propositions:</h4>
                <div className="forms-grid">
                  <div className="form-card">
                    <h5>Theoretical Propositions</h5>
                    <p>General statements at the theory level</p>
                    <span className="form-example">"Leadership influences organizational performance"</span>
                  </div>
                  <div className="form-card">
                    <h5>Empirical Hypotheses</h5>
                    <p>Testable predictions at the study level</p>
                    <span className="form-example">"H1: Transformational leadership is positively correlated with employee productivity"</span>
                  </div>
                  <div className="form-card">
                    <h5>Statistical Hypotheses</h5>
                    <p>Null and alternative hypotheses for testing</p>
                    <span className="form-example">"H0: There is no significant difference between groups"</span>
                  </div>
                </div>
              </div>

              <div className="tip-box">
                <strong>Remember:</strong> Propositions should be derived from your theoretical 
                framework and literature review. They guide your data collection and analysis by 
                telling you what patterns to look for.
              </div>
            </div>
          )}
        </section>

        <section className="framework-section">
          <div 
            className="section-header"
            onClick={() => toggleSection('assumptions')}
          >
            <div className="section-title">
              <div className="section-icon assumptions-icon">
                <Eye size={24} />
              </div>
              <div>
                <h2>4. Assumptions</h2>
                <span className="section-subtitle">Underlying Beliefs</span>
              </div>
            </div>
            {expandedSections.assumptions ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
          </div>

          {expandedSections.assumptions && (
            <div className="section-content">
              <div className="concept-definition">
                <h3>What Are Assumptions?</h3>
                <p>
                  Assumptions are <strong>taken-for-granted premises</strong> that your framework rests on. 
                  They are the foundational beliefs that underpin your research approach.
                </p>
              </div>

              <div className="assumption-examples">
                <h4>Example Assumptions:</h4>
                <div className="assumption-list">
                  <div className="assumption-item">"Human behavior is rational"</div>
                  <div className="assumption-item">"Organizations seek efficiency"</div>
                  <div className="assumption-item">"Context matters for learning"</div>
                  <div className="assumption-item">"Past behavior predicts future behavior"</div>
                </div>
              </div>

              <div className="assumption-categories">
                <h4>Categories of Assumptions:</h4>
                <div className="category-grid">
                  <div className="category-card ontological">
                    <h5>Ontological</h5>
                    <p className="category-desc">Nature of reality</p>
                    <span className="category-example">"Objective reality exists independently of observers"</span>
                  </div>
                  <div className="category-card epistemological">
                    <h5>Epistemological</h5>
                    <p className="category-desc">Nature of knowledge</p>
                    <span className="category-example">"Knowledge is measurable and quantifiable"</span>
                  </div>
                  <div className="category-card axiological">
                    <h5>Axiological</h5>
                    <p className="category-desc">Role of values</p>
                    <span className="category-example">"Research can be value-free and objective"</span>
                  </div>
                  <div className="category-card methodological">
                    <h5>Methodological</h5>
                    <p className="category-desc">How to study phenomena</p>
                    <span className="category-example">"Experiments reveal causal relationships"</span>
                  </div>
                </div>
              </div>

              <div className="critical-box">
                <div className="critical-icon">⚠️</div>
                <div className="critical-content">
                  <strong>Critical Insight:</strong> Assumptions are often <em>IMPLICIT</em>—you may not 
                  even realize you're making them. A key part of rigorous research is making your 
                  assumptions <em>EXPLICIT</em>. This allows readers to evaluate whether they share 
                  your assumptions and helps identify potential limitations in your study.
                </div>
              </div>
            </div>
          )}
        </section>

        <section className="framework-section">
          <div 
            className="section-header"
            onClick={() => toggleSection('boundaries')}
          >
            <div className="section-title">
              <div className="section-icon boundaries-icon">
                <Target size={24} />
              </div>
              <div>
                <h2>5. Boundaries and Scope</h2>
                <span className="section-subtitle">What's In and Out</span>
              </div>
            </div>
            {expandedSections.boundaries ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
          </div>

          {expandedSections.boundaries && (
            <div className="section-content">
              <div className="concept-definition">
                <h3>What Are Boundaries?</h3>
                <p>
                  Boundaries define the <strong>limits and applicability</strong> of your framework. 
                  They clarify where your framework applies—and where it doesn't.
                </p>
              </div>

              <div className="boundary-dimensions">
                <h4>Dimensions of Scope:</h4>
                <div className="dimension-grid">
                  <div className="dimension-card">
                    <div className="dimension-header">
                      <span className="dimension-label">Temporal</span>
                      <span className="dimension-question">When does it apply?</span>
                    </div>
                    <span className="dimension-example">e.g., acute vs. chronic illness, pre-pandemic vs. post-pandemic</span>
                  </div>
                  <div className="dimension-card">
                    <div className="dimension-header">
                      <span className="dimension-label">Spatial</span>
                      <span className="dimension-question">Where does it apply?</span>
                    </div>
                    <span className="dimension-example">e.g., Western cultures vs. global, urban vs. rural settings</span>
                  </div>
                  <div className="dimension-card">
                    <div className="dimension-header">
                      <span className="dimension-label">Population</span>
                      <span className="dimension-question">Who does it apply to?</span>
                    </div>
                    <span className="dimension-example">e.g., adolescents (not adults), first-generation students</span>
                  </div>
                  <div className="dimension-card">
                    <div className="dimension-header">
                      <span className="dimension-label">Contextual</span>
                      <span className="dimension-question">Under what conditions?</span>
                    </div>
                    <span className="dimension-example">e.g., high-stakes vs. low-stakes, formal vs. informal settings</span>
                  </div>
                  <div className="dimension-card">
                    <div className="dimension-header">
                      <span className="dimension-label">Level of Analysis</span>
                      <span className="dimension-question">What unit are you studying?</span>
                    </div>
                    <span className="dimension-example">e.g., individual, team, organization, community, society</span>
                  </div>
                </div>
              </div>

              <div className="scope-example-box">
                <h4>Example Scope Statement:</h4>
                <div className="scope-statement">
                  <p>
                    "This framework applies to <span className="scope-highlight">voluntary employee turnover</span> 
                    [not layoffs] in <span className="scope-highlight">for-profit organizations</span> 
                    [not nonprofits] in <span className="scope-highlight">developed economies</span> 
                    [not developing] during <span className="scope-highlight">stable economic periods</span> 
                    [not recessions]."
                  </p>
                </div>
                <div className="scope-breakdown">
                  <span className="scope-tag">Population: Voluntary leavers</span>
                  <span className="scope-tag">Context: For-profit</span>
                  <span className="scope-tag">Spatial: Developed economies</span>
                  <span className="scope-tag">Temporal: Stable periods</span>
                </div>
              </div>

              <div className="tip-box">
                <strong>Why Boundaries Matter:</strong> Clearly defined boundaries prevent over-generalization 
                of your findings and help future researchers understand when your framework is applicable. 
                They also protect you from criticism about cases your framework was never intended to address.
              </div>
            </div>
          )}
        </section>

        <section className="framework-section">
          <div 
            className="section-header"
            onClick={() => toggleSection('visual')}
          >
            <div className="section-title">
              <div className="section-icon visual-icon">
                <LayoutGrid size={24} />
              </div>
              <div>
                <h2>6. Visual Representation</h2>
                <span className="section-subtitle">The Diagram</span>
              </div>
            </div>
            {expandedSections.visual ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
          </div>

          {expandedSections.visual && (
            <div className="section-content">
              <div className="concept-definition">
                <h3>What Is Visual Representation?</h3>
                <p>
                  A visual representation is a <strong>graphic showing constructs and their relationships</strong>. 
                  It makes your framework tangible and easier to understand at a glance.
                </p>
              </div>

              <div className="customization-note">
                <div className="note-icon">🎨</div>
                <div className="note-content">
                  <strong>Starting Points:</strong> Each format below is just a starting point. You can customize 
                  these diagrams in PowerPoint, Microsoft Word, or other graphical tools to match your specific 
                  framework and visual preferences.
                </div>
              </div>

              <div className="diagram-formats">
                <h4>Common Formats:</h4>
                
                <div className="diagram-format">
                  <div className="format-header">
                    <span className="format-label">A</span>
                    <h5>Box-and-Arrow Diagram</h5>
                  </div>
                  <p className="format-desc">Shows causal relationships, mediators, and moderators in a linear flow.</p>
                  <div className="diagram-box">
                    <pre className="diagram-ascii">{`┌──────────────────┐      ┌───────────┐      ┌──────────────────┐
│ Independent      │      │           │      │ Dependent        │
│ Variable         │─────▶│ Mediator  │─────▶│ Variable         │
└──────────────────┘      └───────────┘      └──────────────────┘
                                ▲
                                │
                          ┌───────────┐
                          │ Moderator │
                          └───────────┘`}</pre>
                  </div>
                  <div className="format-use-case">
                    <strong>Best for:</strong> Testing causal hypotheses, mediation/moderation analysis
                  </div>
                </div>

                <div className="diagram-format">
                  <div className="format-header">
                    <span className="format-label">B</span>
                    <h5>Nested/Hierarchical Model</h5>
                  </div>
                  <p className="format-desc">Shows levels of analysis and how contexts contain smaller units.</p>
                  <div className="diagram-box">
                    <pre className="diagram-ascii">{`┌─────────────────────────────────────────────┐
│  MACRO LEVEL (Context/Society)              │
│  ┌───────────────────────────────────────┐  │
│  │  MESO LEVEL (Organization/Community)  │  │
│  │  ┌─────────────────────────────────┐  │  │
│  │  │  MICRO LEVEL (Individual/Team)  │  │  │
│  │  │                                 │  │  │
│  │  │    ○ Behaviors                  │  │  │
│  │  │    ○ Attitudes                  │  │  │
│  │  │    ○ Outcomes                   │  │  │
│  │  └─────────────────────────────────┘  │  │
│  └───────────────────────────────────────┘  │
└─────────────────────────────────────────────┘`}</pre>
                  </div>
                  <div className="format-use-case">
                    <strong>Best for:</strong> Multi-level research, ecological models, systems thinking
                  </div>
                </div>

                <div className="diagram-format">
                  <div className="format-header">
                    <span className="format-label">C</span>
                    <h5>Process/Stage Model</h5>
                  </div>
                  <p className="format-desc">Shows sequential stages, progression, or feedback loops over time.</p>
                  <div className="diagram-box">
                    <pre className="diagram-ascii">{`┌─────────┐    ┌─────────┐    ┌─────────┐    ┌─────────┐
│ Stage 1 │───▶│ Stage 2 │───▶│ Stage 3 │───▶│ Outcome │
└────┬────┘    └────┬────┘    └────┬────┘    └─────────┘
     │              │              │
     ▼              ▼              ▼
┌────────────────────────────────────────────────────┐
│              Feedback Loop                         │
└────────────────────────────────────────────────────┘`}</pre>
                  </div>
                  <div className="format-use-case">
                    <strong>Best for:</strong> Longitudinal studies, developmental research, iterative processes
                  </div>
                </div>
              </div>

              <div className="best-practices-box">
                <h4>Best Practices for Framework Diagrams:</h4>
                <div className="practices-grid">
                  <div className="practice-item">
                    <span className="practice-number">1</span>
                    <span className="practice-text"><strong>Keep it simple</strong> — maximum 7-9 elements</span>
                  </div>
                  <div className="practice-item">
                    <span className="practice-number">2</span>
                    <span className="practice-text"><strong>Use consistent notation</strong> — same shapes for same types</span>
                  </div>
                  <div className="practice-item">
                    <span className="practice-number">3</span>
                    <span className="practice-text"><strong>Label all arrows</strong> — indicate relationship types</span>
                  </div>
                  <div className="practice-item">
                    <span className="practice-number">4</span>
                    <span className="practice-text"><strong>Include a legend</strong> — explain symbols and notation</span>
                  </div>
                  <div className="practice-item">
                    <span className="practice-number">5</span>
                    <span className="practice-text"><strong>Make it self-explanatory</strong> — understandable without reading text</span>
                  </div>
                </div>
              </div>

              <div className="tip-box">
                <strong>Pro Tip:</strong> Start with a rough sketch on paper, then refine it digitally. 
                Your diagram will likely go through several iterations as your understanding of the 
                relationships deepens through your research.
              </div>
            </div>
          )}
        </section>

        <section className="framework-section">
          <div 
            className="section-header"
            onClick={() => toggleSection('theoretical')}
          >
            <div className="section-title">
              <div className="section-icon theoretical-icon">
                <GraduationCap size={24} />
              </div>
              <div>
                <h2>7. Theoretical Foundation</h2>
                <span className="section-subtitle">Where It Comes From</span>
              </div>
            </div>
            {expandedSections.theoretical ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
          </div>

          {expandedSections.theoretical && (
            <div className="section-content">
              <div className="concept-definition">
                <h3>What Is Theoretical Foundation?</h3>
                <p>
                  Your theoretical foundation describes the <strong>underlying theories or prior frameworks</strong> that 
                  inform your research. It shows the intellectual lineage of your work.
                </p>
              </div>

              <div className="theory-examples">
                <h4>How Theories Connect to Your Framework:</h4>
                <div className="theory-connection-list">
                  <div className="theory-connection">
                    <span className="connection-type">Built on:</span>
                    <span className="connection-example">Social Cognitive Theory (Bandura)</span>
                  </div>
                  <div className="theory-connection">
                    <span className="connection-type">Extends:</span>
                    <span className="connection-example">Technology Acceptance Model (Davis)</span>
                  </div>
                  <div className="theory-connection">
                    <span className="connection-type">Integrates:</span>
                    <span className="connection-example">Self-Determination Theory + Expectancy-Value Theory</span>
                  </div>
                  <div className="theory-connection">
                    <span className="connection-type">Adapts:</span>
                    <span className="connection-example">Diffusion of Innovations (Rogers) to healthcare context</span>
                  </div>
                </div>
              </div>

              <div className="tip-box">
                <strong>Tip:</strong> You can document your theoretical foundations in <strong>Wizard C → Step 3</strong>, 
                where they'll be saved as part of your framework design.
              </div>

              <div className="theory-functions">
                <h4>Why Theoretical Foundation Matters:</h4>
                <div className="functions-grid">
                  <div className="function-item">
                    <span className="function-icon">✓</span>
                    <span><strong>Establishes credibility</strong> — shows your work is grounded in established knowledge</span>
                  </div>
                  <div className="function-item">
                    <span className="function-icon">✓</span>
                    <span><strong>Shows intellectual lineage</strong> — connects your work to the scholarly conversation</span>
                  </div>
                  <div className="function-item">
                    <span className="function-icon">✓</span>
                    <span><strong>Justifies your choices</strong> — explains why you've structured your framework this way</span>
                  </div>
                  <div className="function-item">
                    <span className="function-icon">✓</span>
                    <span><strong>Identifies gaps you're filling</strong> — shows your unique contribution</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </section>

        <section className="framework-section">
          <div 
            className="section-header"
            onClick={() => toggleSection('context')}
          >
            <div className="section-title">
              <div className="section-icon context-icon">
                <Settings size={24} />
              </div>
              <div>
                <h2>8. Context and Conditions</h2>
                <span className="section-subtitle">When/Where It Applies</span>
              </div>
            </div>
            {expandedSections.context ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
          </div>

          {expandedSections.context && (
            <div className="section-content">
              <div className="concept-definition">
                <h3>What Are Context and Conditions?</h3>
                <p>
                  These are the <strong>factors that enable or constrain</strong> your framework's application. 
                  They specify when your framework works best and when it might not apply.
                </p>
              </div>

              <div className="conditions-types">
                <div className="condition-category enabling">
                  <div className="category-header">
                    <span className="condition-badge enabling-badge">Enabling Conditions</span>
                    <span className="condition-desc">Factors that must be present for your framework to work</span>
                  </div>
                  <div className="condition-examples">
                    <div className="condition-item">"This framework works when trust is high"</div>
                    <div className="condition-item">"Assumes access to resources"</div>
                    <div className="condition-item">"Requires stable environment"</div>
                  </div>
                </div>

                <div className="condition-category boundary">
                  <div className="category-header">
                    <span className="condition-badge boundary-badge">Boundary Conditions</span>
                    <span className="condition-desc">Situations where your framework may not apply</span>
                  </div>
                  <div className="condition-examples">
                    <div className="condition-item">"May not apply in crisis situations"</div>
                    <div className="condition-item">"Limited to literate populations"</div>
                    <div className="condition-item">"Assumes Western cultural values"</div>
                  </div>
                </div>

                <div className="condition-category contingency">
                  <div className="category-header">
                    <span className="condition-badge contingency-badge">Contingencies</span>
                    <span className="condition-desc">Factors that modify how relationships work</span>
                  </div>
                  <div className="condition-examples">
                    <div className="condition-item">"Effect stronger for younger employees"</div>
                    <div className="condition-item">"Relationship reversed under high stress"</div>
                    <div className="condition-item">"Impact varies by organizational size"</div>
                  </div>
                </div>
              </div>

              <div className="tip-box">
                <strong>Why This Matters:</strong> Being explicit about context and conditions demonstrates 
                scholarly rigor. It shows you understand your framework's limitations and helps other 
                researchers know when they can apply your findings to their own contexts.
              </div>
            </div>
          )}
        </section>

        <section className="framework-section">
          <div 
            className="section-header"
            onClick={() => toggleSection('levels')}
          >
            <div className="section-title">
              <div className="section-icon levels-icon">
                <Layers size={24} />
              </div>
              <div>
                <h2>9. Levels of Analysis</h2>
                <span className="section-subtitle">Scale/Unit</span>
              </div>
            </div>
            {expandedSections.levels ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
          </div>

          {expandedSections.levels && (
            <div className="section-content">
              <div className="concept-definition">
                <h3>What Are Levels of Analysis?</h3>
                <p>
                  Levels of analysis define the <strong>scale or unit</strong> at which your framework operates. 
                  Research can focus on individuals, groups, organizations, or entire societies—and sometimes 
                  multiple levels at once.
                </p>
              </div>

              <div className="levels-selection">
                <h4>Common Levels of Analysis</h4>
                <p className="levels-intro">
                  Research can operate at different scales. Here are the common levels you may consider for your framework:
                </p>

                <div className="levels-table levels-table-readonly">
                  <div className="levels-header">
                    <span className="col-level">Level</span>
                    <span className="col-unit">Unit of Analysis</span>
                    <span className="col-examples">Example Constructs</span>
                  </div>
                  {analysisLevels.map((level, index) => (
                    <div 
                      key={level.id} 
                      className="levels-row"
                    >
                      <span className="col-level">
                        <span className="level-number">{index + 1}</span>
                        {level.level}
                      </span>
                      <span className="col-unit">{level.unit}</span>
                      <span className="col-examples">{level.examples}</span>
                    </div>
                  ))}
                </div>

                <div className="tip-box">
                  <strong>Tip:</strong> You can select levels for your framework in <strong>Wizard C → Step 2</strong>. 
                  We recommend choosing no more than 3 levels to keep your framework focused.
                </div>
              </div>

              <div className="cross-level-box">
                <h4>Cross-Level Frameworks</h4>
                <p>
                  Some frameworks span multiple levels, showing how factors at one level influence outcomes at another. 
                  This is called <strong>cross-level</strong> or <strong>multi-level</strong> analysis.
                </p>
                <div className="cross-level-example">
                  <span className="cross-level-flow">
                    <span className="flow-item org">Organizational Culture</span>
                    <span className="flow-arrow">→</span>
                    <span className="flow-item team">Team Norms</span>
                    <span className="flow-arrow">→</span>
                    <span className="flow-item individual">Individual Behavior</span>
                  </span>
                </div>
                <p className="cross-level-note">
                  This example shows how macro-level factors cascade down to influence micro-level outcomes.
                </p>
              </div>

              <div className="tip-box">
                <strong>Key Consideration:</strong> Multi-level frameworks require appropriate data collection 
                at each level and statistical techniques (like Hierarchical Linear Modeling) that can handle 
                nested data structures.
              </div>
            </div>
          )}
        </section>
      </div>

      <div className="framework-reference-sections">
        <section className="framework-section criteria-section">
          <div className="section-header always-expanded">
            <div className="section-title">
              <div className="section-icon criteria-icon">
                <CheckCircle size={24} />
              </div>
              <div>
                <h2>Criteria for a Good Framework</h2>
                <span className="section-subtitle">Quality Checklist</span>
              </div>
            </div>
          </div>

          <div className="section-content">
            <p className="criteria-intro">A strong framework should meet these 10 criteria:</p>
            
            <div className="criteria-table">
              <div className="criteria-header">
                <span className="col-criterion">Criterion</span>
                <span className="col-description">Description</span>
                <span className="col-test">Test Question</span>
              </div>
              <div className="criteria-row">
                <span className="col-criterion"><strong>Clear</strong></span>
                <span className="col-description">Unambiguous definitions</span>
                <span className="col-test">Can readers understand without asking questions?</span>
              </div>
              <div className="criteria-row">
                <span className="col-criterion"><strong>Logical</strong></span>
                <span className="col-description">Coherent relationships</span>
                <span className="col-test">Do the connections make sense?</span>
              </div>
              <div className="criteria-row">
                <span className="col-criterion"><strong>Parsimonious</strong></span>
                <span className="col-description">As simple as possible</span>
                <span className="col-test">Can I remove anything without losing meaning?</span>
              </div>
              <div className="criteria-row">
                <span className="col-criterion"><strong>Comprehensive</strong></span>
                <span className="col-description">Covers all relevant elements</span>
                <span className="col-test">Are any important factors missing?</span>
              </div>
              <div className="criteria-row">
                <span className="col-criterion"><strong>Testable</strong></span>
                <span className="col-description">Can be empirically examined</span>
                <span className="col-test">Can I collect data to test this?</span>
              </div>
              <div className="criteria-row">
                <span className="col-criterion"><strong>Grounded</strong></span>
                <span className="col-description">Based on theory/evidence</span>
                <span className="col-test">What literature supports this?</span>
              </div>
              <div className="criteria-row">
                <span className="col-criterion"><strong>Novel</strong></span>
                <span className="col-description">Adds something new</span>
                <span className="col-test">How is this different from existing frameworks?</span>
              </div>
              <div className="criteria-row">
                <span className="col-criterion"><strong>Useful</strong></span>
                <span className="col-description">Practical value</span>
                <span className="col-test">Does this help solve real problems?</span>
              </div>
              <div className="criteria-row">
                <span className="col-criterion"><strong>Bounded</strong></span>
                <span className="col-description">Clear scope</span>
                <span className="col-test">Where does this NOT apply?</span>
              </div>
              <div className="criteria-row">
                <span className="col-criterion"><strong>Visual</strong></span>
                <span className="col-description">Well-diagrammed</span>
                <span className="col-test">Can I explain this with a picture?</span>
              </div>
            </div>
          </div>
        </section>

        <section className="framework-section examples-section">
          <div className="section-header always-expanded">
            <div className="section-title">
              <div className="section-icon examples-icon">
                <Library size={24} />
              </div>
              <div>
                <h2>Examples of Well-Known Frameworks</h2>
                <span className="section-subtitle">Learn from the Best</span>
              </div>
            </div>
          </div>

          <div className="section-content">
            <div className="famous-frameworks">
              <div className="famous-framework-card tam">
                <h4>1. Technology Acceptance Model (TAM)</h4>
                <div className="framework-detail">
                  <span className="detail-label">Constructs:</span>
                  <span>Perceived Usefulness, Perceived Ease of Use, Attitude, Behavioral Intention, Actual Use</span>
                </div>
                <div className="framework-detail">
                  <span className="detail-label">Relationship:</span>
                  <span>PU + PEOU → Attitude → Intention → Use</span>
                </div>
                <div className="framework-detail">
                  <span className="detail-label">Assumptions:</span>
                  <span>Users are rational, attitudes drive behavior</span>
                </div>
                <div className="framework-detail">
                  <span className="detail-label">Boundary:</span>
                  <span>Voluntary technology adoption in workplace</span>
                </div>
              </div>

              <div className="famous-framework-card ecological">
                <h4>2. Social-Ecological Model (Bronfenbrenner)</h4>
                <div className="framework-detail">
                  <span className="detail-label">Constructs:</span>
                  <span>Microsystem, Mesosystem, Exosystem, Macrosystem, Chronosystem</span>
                </div>
                <div className="framework-detail">
                  <span className="detail-label">Relationship:</span>
                  <span>Nested levels of influence</span>
                </div>
                <div className="framework-detail">
                  <span className="detail-label">Assumptions:</span>
                  <span>Development occurs through interaction with environment</span>
                </div>
                <div className="framework-detail">
                  <span className="detail-label">Boundary:</span>
                  <span>Human development across lifespan</span>
                </div>
              </div>

              <div className="famous-framework-card tpb">
                <h4>3. Theory of Planned Behavior (Ajzen)</h4>
                <div className="framework-detail">
                  <span className="detail-label">Constructs:</span>
                  <span>Attitude, Subjective Norm, Perceived Behavioral Control → Intention → Behavior</span>
                </div>
                <div className="framework-detail">
                  <span className="detail-label">Relationship:</span>
                  <span>Three factors predict intention, which predicts behavior</span>
                </div>
                <div className="framework-detail">
                  <span className="detail-label">Assumptions:</span>
                  <span>Behavior is planned and rational</span>
                </div>
                <div className="framework-detail">
                  <span className="detail-label">Boundary:</span>
                  <span>Voluntary behaviors under volitional control</span>
                </div>
              </div>

              <div className="famous-framework-card jdr">
                <h4>4. Job Demands-Resources Model</h4>
                <div className="framework-detail">
                  <span className="detail-label">Constructs:</span>
                  <span>Job Demands, Job Resources → Strain/Motivation → Outcomes</span>
                </div>
                <div className="framework-detail">
                  <span className="detail-label">Relationship:</span>
                  <span>Two parallel processes (health impairment & motivational)</span>
                </div>
                <div className="framework-detail">
                  <span className="detail-label">Assumptions:</span>
                  <span>All jobs have demands and resources</span>
                </div>
                <div className="framework-detail">
                  <span className="detail-label">Boundary:</span>
                  <span>Occupational wellbeing and performance</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="framework-section distinction-section">
          <div className="section-header always-expanded">
            <div className="section-title">
              <div className="section-icon distinction-icon">
                <HelpCircle size={24} />
              </div>
              <div>
                <h2>Framework vs. Theory vs. Model</h2>
                <span className="section-subtitle">Understanding the Distinction</span>
              </div>
            </div>
          </div>

          <div className="section-content">
            <p className="distinction-intro">These terms are often confused—here's the distinction:</p>
            
            <div className="distinction-table">
              <div className="distinction-header">
                <span className="col-term">Term</span>
                <span className="col-def">Definition</span>
                <span className="col-scope">Scope</span>
                <span className="col-example">Example</span>
              </div>
              <div className="distinction-row theory-row">
                <span className="col-term"><strong>Theory</strong></span>
                <span className="col-def">Systematic explanation with laws/principles</span>
                <span className="col-scope">Broad, general</span>
                <span className="col-example">Self-Determination Theory</span>
              </div>
              <div className="distinction-row framework-row">
                <span className="col-term"><strong>Framework</strong></span>
                <span className="col-def">Structured way to organize concepts</span>
                <span className="col-scope">Medium, flexible</span>
                <span className="col-example">Your conceptual framework for a study</span>
              </div>
              <div className="distinction-row model-row">
                <span className="col-term"><strong>Model</strong></span>
                <span className="col-def">Simplified representation</span>
                <span className="col-scope">Narrow, specific</span>
                <span className="col-example">Regression model, path model</span>
              </div>
            </div>

            <div className="hierarchy-box">
              <h4>Hierarchy of Abstraction</h4>
              <div className="hierarchy-visual">
                <div className="hierarchy-level level-theory">
                  <span className="hierarchy-label">Theory</span>
                  <span className="hierarchy-desc">Most abstract & general</span>
                </div>
                <div className="hierarchy-arrow">▼</div>
                <div className="hierarchy-level level-framework">
                  <span className="hierarchy-label">Framework</span>
                  <span className="hierarchy-desc">Organizes concepts flexibly</span>
                </div>
                <div className="hierarchy-arrow">▼</div>
                <div className="hierarchy-level level-model">
                  <span className="hierarchy-label">Model</span>
                  <span className="hierarchy-desc">Most specific & testable</span>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      <div className="framework-complete">
        <h3>Framework Learning Complete!</h3>
        <p>
          You've explored all 9 core aspects of research frameworks, plus quality criteria and real-world examples. 
          Use this knowledge to build a strong, well-structured framework for your own research.
        </p>
        
        <label className={`framework-understanding-checkbox ${frameworkComplete ? 'checked' : ''}`}>
          <input
            type="checkbox"
            checked={frameworkComplete}
            onChange={(e) => setFrameworkComplete(e.target.checked)}
          />
          <span className="checkbox-box">
            {frameworkComplete && <Check size={16} />}
          </span>
          <span className="checkbox-label">I now understand research frameworks</span>
        </label>
      </div>

      <div className="launch-wizard-c">
        <div className="launch-content">
          <div className="launch-icon">
            <Boxes size={48} />
          </div>
          <div className="launch-text">
            <h3>Ready to Build Your Framework?</h3>
            <p>
              Use Wizard C to create your own conceptual framework with drag-and-drop diagram building, 
              automatic proposition generation, and export capabilities.
            </p>
          </div>
          <button className="launch-btn" onClick={handleLaunchWizardC}>
            <span>Wizard C - Framework Builder</span>
            <ArrowRight size={20} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default FrameworkPage;
